import axios, { AxiosResponse, AxiosError } from "axios";
import { API_CONFIG, API_ENDPOINTS, ERROR_CODES } from "@/constants";
import { ApiResponse, ApiError, SubscriptionStatusResponse } from "@/types";
import type {
  ComposerPiiDetectRequest,
  ComposerPiiDetectResponse,
  ChatMessageRequest,
  TranscriptResponse,
  TranscriptMessage,
} from "@/features/chat/types";
import { KnowledgeFileListResponse } from "@/features/knowledgeFiles";
import type { MaintenanceApiResponse } from "@/features/maintenance/types";
import type {
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  OnboardingCompleteRequestBody,
  OnboardingCompleteResponse,
  OrgDisclaimerAcceptResponse,
  EntraExchangeRequest,
  SocialAuthProvider,
  SocialLinkRequestBody,
  SocialLoginResult,
  SocialSessionToken,
  UserProfileResponse,
} from "@/features/auth/types/authTypes";

export const getApiBaseUrl = (): string => {
  if (typeof process !== "undefined" && process.env.NODE_ENV === "test") {
    return process.env.VITE_API_BASE_URL || "http://localhost:8000";
  }
  const fromGlobal = (
    globalThis as { importMeta?: { env?: { VITE_API_BASE_URL?: string } } }
  ).importMeta?.env?.VITE_API_BASE_URL;
  if (fromGlobal) {
    return fromGlobal;
  }
  return "http://localhost:8000";
};

function parseSocialLoginResponse(data: unknown): SocialLoginResult {
  if (!data || typeof data !== "object") {
    throw new Error("Invalid social login response");
  }
  const d = data as Record<string, unknown>;
  if (d.status === "linking_required") {
    const provider = typeof d.provider === "string" ? d.provider : "";
    return {
      kind: "linking_required",
      linking: {
        status: "linking_required",
        provider,
        email_hint:
          typeof d.email_hint === "string" ? d.email_hint : undefined,
        message: typeof d.message === "string" ? d.message : undefined,
      },
    };
  }
  if (typeof d.access_token === "string") {
    return {
      kind: "session",
      token: data as SocialSessionToken,
    };
  }
  throw new Error("Invalid social login response");
}

function isApiErrorShape(error: unknown): error is ApiError {
  return (
    !axios.isAxiosError(error) &&
    !!error &&
    typeof error === "object" &&
    "code" in error &&
    typeof (error as ApiError).code === "string" &&
    "message" in error &&
    typeof (error as ApiError).message === "string"
  );
}

/** Wire JSON body: required fields + optional BE fields only when set */
function serializeSocialLinkRequestBody(
  body: SocialLinkRequestBody
): Record<string, string | null> {
  const payload: Record<string, string | null> = {
    provider: body.provider,
    provider_token: body.provider_token,
  };
  if (body.platform !== undefined) {
    payload.platform = body.platform;
  }
  if (body.apple_given_name !== undefined) {
    payload.apple_given_name = body.apple_given_name;
  }
  if (body.apple_family_name !== undefined) {
    payload.apple_family_name = body.apple_family_name;
  }
  return payload;
}

function assertSocialLinkTokenResponse(data: unknown): SocialSessionToken {
  if (
    !data ||
    typeof data !== "object" ||
    typeof (data as { access_token?: unknown }).access_token !== "string" ||
    typeof (data as { token_type?: unknown }).token_type !== "string"
  ) {
    throw new Error("Invalid social link response");
  }
  return data as SocialSessionToken;
}

function assertOnboardingCompleteResponse(
  data: unknown,
  statusCode?: number
): OnboardingCompleteResponse {
  // Some backend environments return 204 with empty body for successful completion.
  if (data === undefined || data === null || data === "") {
    return { onboarding_completed: true };
  }

  if (typeof data === "object") {
    const record = data as Record<string, unknown>;

    if (typeof record.onboarding_completed === "boolean") {
      return {
        onboarding_completed: record.onboarding_completed,
        message:
          typeof record.message === "string" ? record.message : undefined,
      };
    }

    if (typeof record.onboardingComplete === "boolean") {
      return {
        onboarding_completed: record.onboardingComplete,
        message:
          typeof record.message === "string" ? record.message : undefined,
      };
    }

    if (typeof record.success === "boolean") {
      return {
        onboarding_completed: record.success,
        message:
          typeof record.message === "string" ? record.message : undefined,
      };
    }

    // Defensive fallback: some environments return non-standard success
    // payloads after completion (e.g. metadata/user object only).
    if (
      typeof statusCode === "number" &&
      statusCode >= 200 &&
      statusCode < 300
    ) {
      const message =
        typeof record.message === "string"
          ? record.message
          : typeof record.detail === "string"
            ? record.detail
            : undefined;
      return { onboarding_completed: true, message };
    }
  }

  if (
    typeof statusCode === "number" &&
    statusCode >= 200 &&
    statusCode < 300
  ) {
    return { onboarding_completed: true };
  }

  throw new Error("Invalid onboarding complete response");
}

// SECURITY: Import auth context to get tokens from memory instead of localStorage
// This prevents XSS attacks that could steal tokens from localStorage
let getAuthToken: (() => string | null) | null = null;
let getRefreshToken: (() => string | null) | null = null;
let updateTokensCallback:
  | ((accessToken: string, refreshToken: string) => void)
  | null = null;
let logoutCallback: (() => void) | null = null;
let subscriptionExpiredCallback:
  | ((error?: ApiError) => void | Promise<void>)
  | null = null;
let orgDisclaimerRequiredCallback: (() => void | Promise<void>) | null = null;

function getResponseErrorKey(data: unknown): string | undefined {
  if (!data || typeof data !== "object") {
    return undefined;
  }
  const record = data as Record<string, unknown>;
  if (typeof record.error_key === "string") {
    return record.error_key;
  }
  const detail = record.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const detailKey = (detail as Record<string, unknown>).error_key;
    if (typeof detailKey === "string") {
      return detailKey;
    }
  }
  return undefined;
}

function getResponseOrgDisclaimer(data: unknown): unknown {
  if (!data || typeof data !== "object") {
    return undefined;
  }
  const record = data as Record<string, unknown>;
  if (record.org_disclaimer !== undefined) {
    return record.org_disclaimer;
  }
  const detail = record.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return (detail as Record<string, unknown>).org_disclaimer;
  }
  return undefined;
}

function getBackendErrorData(responseData: any): any {
  const orgDisclaimer = getResponseOrgDisclaimer(responseData);
  if (responseData?.detail) {
    if (typeof responseData.detail === "string") {
      return {
        error_key: responseData.error_key,
        params: responseData.params,
        detail: responseData.detail,
        org_disclaimer: orgDisclaimer,
      };
    }
    if (Array.isArray(responseData.detail) && responseData.detail.length > 0) {
      const firstError = responseData.detail[0];
      if (firstError.error_key) {
        return {
          error_key: firstError.error_key,
          params:
            firstError.ctx ||
            firstError.params || {
              field: firstError.loc?.[firstError.loc.length - 1] || "field",
              max_length: firstError.ctx?.max_length,
              min_length: firstError.ctx?.min_length,
              ...firstError.ctx,
            },
          detail: firstError.msg || firstError.message || "Validation error",
          org_disclaimer: orgDisclaimer,
        };
      }
      return {
        error_key: responseData.error_key,
        params: responseData.params,
        detail: firstError.msg || firstError.message || "Validation error",
        org_disclaimer: orgDisclaimer,
      };
    }
  }

  return {
    error_key: getResponseErrorKey(responseData) || responseData?.error_key,
    params: responseData?.params,
    detail: responseData?.message || responseData?.error || "An error occurred",
    org_disclaimer: orgDisclaimer,
  };
}

function createApiErrorFromResponse(
  status: number,
  data: unknown,
  fallbackMessage: string
): ApiError {
  const backendData = getBackendErrorData(data);
  const code =
    status === 430
      ? ERROR_CODES.SUBSCRIPTION_EXPIRED
      : status === 403
        ? ERROR_CODES.FORBIDDEN
        : String(status);

  return {
    code,
    message: backendData.detail || fallbackMessage,
    backendData,
  };
}

// Function to set the auth token getter from AuthContext
// This will be called by the AuthProvider to provide secure token access
export const setAuthTokenGetter = (tokenGetter: () => string | null) => {
  getAuthToken = tokenGetter;
};

// Function to set the refresh token getter from AuthContext
export const setRefreshTokenGetter = (tokenGetter: () => string | null) => {
  getRefreshToken = tokenGetter;
};

// Function to set the token update callback from AuthContext
export const setUpdateTokensCallback = (
  callback: (accessToken: string, refreshToken: string) => void
) => {
  updateTokensCallback = callback;
};

// Function to set the logout callback from AuthContext
export const setLogoutCallback = (callback: () => void) => {
  logoutCallback = callback;
};

// Function to set the subscription expired callback from AuthContext
// This will be called when backend returns 430 (subscription/trial expired)
export const setSubscriptionExpiredCallback = (
  callback: ((error?: ApiError) => void | Promise<void>) | null
) => {
  subscriptionExpiredCallback = callback;
};

export const setOrgDisclaimerRequiredCallback = (
  callback: (() => void | Promise<void>) | null
) => {
  orgDisclaimerRequiredCallback = callback;
};

// Helper function to attempt token refresh for fetch-based requests
// This is needed for streaming endpoints that don't use Axios interceptors
const attemptTokenRefresh = async (): Promise<string | null> => {
  try {
    const currentRefreshToken = getRefreshToken?.();
    if (!currentRefreshToken) {
      // Don't create error_key on frontend - just throw a simple error
      // The backend will provide error_key when it responds
      throw new Error("No refresh token available");
    }

    const response = await api.post("/api/v1/login/refresh-token", {
      refresh_token: currentRefreshToken,
    });

    const { access_token, refresh_token } = response.data;

    // Update tokens in AuthContext
    if (updateTokensCallback) {
      updateTokensCallback(access_token, refresh_token);
    }

    return access_token;
  } catch (error) {
    // Refresh failed - trigger logout
    if (logoutCallback) {
      logoutCallback();
    }
    return null;
  }
};

// Create axios instance (exported for tests that attach axios-mock-adapter)
export const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // SECURITY: Get token from memory (AuthContext) instead of localStorage
    const token = getAuthToken?.() || null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // TODO: When backend implements HttpOnly cookies, remove token handling entirely
    // and just set credentials: 'include' for all requests:
    // config.credentials = 'include';

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Response interceptor to handle errors and refresh tokens
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;
    const errorMessage = (error.response?.data as any)?.detail || "";
    const requestUrl: string = originalRequest?.url ?? "";

    // Handle 401 errors (token expired or revoked)
    if (error.response?.status === 401) {
      // Login / social sign-in: no session yet — do not run refresh-token flow
      if (
        requestUrl.includes("/api/v1/auth/social") ||
        requestUrl.includes("/api/v1/auth/entra/exchange") ||
        requestUrl.includes("/api/v1/auth/invitations/accept") ||
        requestUrl.includes("/api/v1/login/access-token")
      ) {
        return Promise.reject(error);
      }

      // Check if token has been revoked (blacklisted after logout)
      if (
        errorMessage.toLowerCase().includes("revoked") ||
        errorMessage.toLowerCase().includes("blacklisted")
      ) {
        // Token is revoked - force logout without retry
        if (logoutCallback) {
          logoutCallback();
        }
        return Promise.reject(error);
      }

      // Token expired - try to refresh if not already retrying
      if (!originalRequest._retry) {
        if (isRefreshing) {
          // Another request is already refreshing - queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(() => {
              return api(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Attempt to refresh the token
          const currentRefreshToken = getRefreshToken?.();
          if (!currentRefreshToken) {
            // Don't create error_key on frontend
            throw new Error("No refresh token available");
          }

          const response = await api.post("/api/v1/login/refresh-token", {
            refresh_token: currentRefreshToken,
          });

          const { access_token, refresh_token } = response.data;

          // Update tokens in AuthContext
          if (updateTokensCallback) {
            updateTokensCallback(access_token, refresh_token);
          }

          // Process queued requests with new token
          processQueue(null, access_token);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed - logout user
          processQueue(refreshError, null);
          if (logoutCallback) {
            logoutCallback();
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    }

    const responseErrorKey = getResponseErrorKey(error.response?.data);

    if (
      error.response?.status === 403 &&
      responseErrorKey === "ORG_DISCLAIMER_REQUIRED"
    ) {
      if (orgDisclaimerRequiredCallback) {
        orgDisclaimerRequiredCallback();
      }
      return Promise.reject(error);
    }

    // Handle subscription/access gating responses through AuthContext.
    if (
      error.response?.status === 430 ||
      (error.response?.status === 403 &&
        responseErrorKey === "ERROR_ORGANIZATION_INACTIVE")
    ) {
      if (subscriptionExpiredCallback) {
        subscriptionExpiredCallback(
          createApiErrorFromResponse(
            error.response.status,
            error.response.data,
            "Access is unavailable"
          )
        );
        (error as AxiosError & { accessGateCallbackNotified?: boolean })
          .accessGateCallbackNotified = true;
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// API helper functions
export const apiClient = {
  get: async <T>(
    url: string,
    params?: Record<string, any>
  ): Promise<ApiResponse<T>> => {
    try {
      const response = await api.get(url, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  post: async <T>(url: string, data?: any): Promise<ApiResponse<T>> => {
    try {
      const response = await api.post(url, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  put: async <T>(url: string, data?: any): Promise<ApiResponse<T>> => {
    try {
      const response = await api.put(url, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  patch: async <T>(url: string, data?: any): Promise<ApiResponse<T>> => {
    try {
      const response = await api.patch(url, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  delete: async <T>(url: string): Promise<ApiResponse<T>> => {
    try {
      const response = await api.delete(url);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  upload: async <T>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<T>> => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(progress);
          }
        },
      });

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Error handling
function handleApiError(error: unknown): ApiError {
  // TODO: When implementing user feedback, show global error notifications:
  // import { toast } from "@/components/ui/use-toast";
  //
  // For critical errors that should always show to user:
  // - Network errors
  // - Authentication failures
  // - Server errors (500s)
  //
  // Example:
  // if (isNetworkError || isServerError) {
  //   toast({
  //     variant: "destructive",
  //     title: "System Error",
  //     description: errorMessage,
  //   });
  // }

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;

    // Network error
    if (!axiosError.response) {
      return {
        code: ERROR_CODES.NETWORK_ERROR,
        message: "Network error. Please check your connection.",
      };
    }

    // Server response error
    const { status, data } = axiosError.response;

    // Extract backend error data (error_key + params for translation)
    const backendData = getBackendErrorData(data);

    switch (status) {
      case 400:
        return {
          code: ERROR_CODES.VALIDATION_ERROR,
          message: backendData.detail || "Invalid request data",
          details: (data as any)?.details,
          backendData, // Include raw backend data for translation
        };
      case 401:
        return {
          code: ERROR_CODES.UNAUTHORIZED,
          message: backendData.detail || "Authentication required",
          backendData,
        };
      case 403:
        {
          const apiError = {
            code: ERROR_CODES.FORBIDDEN,
            message: backendData.detail || "Access denied",
            backendData,
          };
          const wasAccessGateCallbackNotified =
            (axiosError as AxiosError & { accessGateCallbackNotified?: boolean })
              .accessGateCallbackNotified === true;
          if (
            backendData.error_key === "ORG_DISCLAIMER_REQUIRED" &&
            orgDisclaimerRequiredCallback
          ) {
            orgDisclaimerRequiredCallback();
          }
          if (
            backendData.error_key === "ERROR_ORGANIZATION_INACTIVE" &&
            subscriptionExpiredCallback &&
            !wasAccessGateCallbackNotified
          ) {
            subscriptionExpiredCallback(apiError);
          }
          return apiError;
        }
      case 404:
        return {
          code: ERROR_CODES.NOT_FOUND,
          message: backendData.detail || "Resource not found",
          backendData,
        };
      case 409:
        return {
          code: ERROR_CODES.CONFLICT,
          message: backendData.detail || "Conflict",
          backendData,
        };
      case 422:
         // FastAPI validation errors
         if (backendData.detail.toLowerCase().includes("speech was detected")) {
          return {
            code: ERROR_CODES.ERROR_VOICE_NO_SPEECH_DETECTED,
            message: "No speech was detected in the audio. Please speak clearly and try again.",
            backendData,
          };
         }
        return {
          code: ERROR_CODES.VALIDATION_ERROR,
          message: backendData.detail || "Validation error",
          details: (data as any)?.detail,
          backendData,
        };
      case 429:
        return {
          code: ERROR_CODES.SUBSCRIPTION_LIMIT_REACHED,
          message: backendData.detail || "Rate limit exceeded",
          backendData,
        };
      case 430:
        {
          const apiError = {
            code: ERROR_CODES.SUBSCRIPTION_EXPIRED,
            message:
              backendData.detail ||
              "Your subscription or trial has expired. Please upgrade to continue.",
            backendData,
          };
          const wasAccessGateCallbackNotified =
            (axiosError as AxiosError & { accessGateCallbackNotified?: boolean })
              .accessGateCallbackNotified === true;
          if (subscriptionExpiredCallback && !wasAccessGateCallbackNotified) {
            subscriptionExpiredCallback(apiError);
          }
          return apiError;
        }
      case 500:
      default:
        return {
          code: ERROR_CODES.SERVER_ERROR,
          message: backendData.detail || "Internal server error",
          backendData,
        };
    }
  }

  // Unknown error
  return {
    code: ERROR_CODES.SERVER_ERROR,
    message:
      error instanceof Error ? error.message : "An unknown error occurred",
  };
}

// Specific API functions
export const authApi = {
  login: async (email: string, password: string, org?: string | null) => {
    const formData = new URLSearchParams();
    formData.append("username", email);
    formData.append("password", password);
    formData.append("grant_type", "password");
    formData.append("scope", "");

    const url = org
      ? `/api/v1/login/access-token?org=${encodeURIComponent(org)}`
      : "/api/v1/login/access-token";

    try {
      const response = await api.post(url, formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  socialLogin: async (
    provider: SocialAuthProvider,
    providerToken: string,
    org?: string | null
  ): Promise<SocialLoginResult> => {
    try {
      const body: Record<string, string> = {
        provider_token: providerToken,
      };
      if (org) {
        body.org = org;
      }
      const response = await api.post(
        `/api/v1/auth/social/${provider}`,
        body
      );
      return parseSocialLoginResponse(response.data);
    } catch (error: unknown) {
      if (isApiErrorShape(error)) {
        throw error;
      }
      throw handleApiError(error);
    }
  },

  socialLink: async (
    body: SocialLinkRequestBody
  ): Promise<SocialSessionToken> => {
    try {
      const response = await api.post(
        "/api/v1/auth/social/link",
        serializeSocialLinkRequestBody(body)
      );
      return assertSocialLinkTokenResponse(response.data);
    } catch (error: unknown) {
      if (isApiErrorShape(error)) {
        throw error;
      }
      throw handleApiError(error);
    }
  },

  entraExchange: async (
    body: EntraExchangeRequest
  ): Promise<SocialSessionToken> => {
    try {
      const response = await api.post("/api/v1/auth/entra/exchange", {
        code: body.code,
      });
      return assertSocialLinkTokenResponse(response.data);
    } catch (error: unknown) {
      if (isApiErrorShape(error)) {
        throw error;
      }
      throw handleApiError(error);
    }
  },

  acceptInvitation: async (
    body: AcceptInvitationRequest
  ): Promise<AcceptInvitationResponse> => {
    try {
      const response = await api.post(
        "/api/v1/auth/invitations/accept",
        body
      );
      return response.data;
    } catch (error: unknown) {
      if (isApiErrorShape(error)) {
        throw error;
      }
      throw handleApiError(error);
    }
  },

  completeOnboarding: async (
    body: OnboardingCompleteRequestBody
  ): Promise<OnboardingCompleteResponse> => {
    try {
      const response = await api.post(
        "/api/v1/auth/account/complete",
        body
      );
      return assertOnboardingCompleteResponse(response.data, response.status);
    } catch (error: unknown) {
      if (isApiErrorShape(error)) {
        throw error;
      }
      throw handleApiError(error);
    }
  },

  testToken: async () => {
    try {
      const response = await api.post("/api/v1/login/test-token");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  register: async (userData: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    date_of_birth: string;
    terms_accepted: boolean;
    privacy_policy_accepted: boolean;
    language: string;
    theme: string;
  }) => {
    try {
      const response = await api.post("/api/v1/users/signup", userData);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getCurrentUser: async (): Promise<UserProfileResponse> => {
    try {
      const response = await api.get("/api/v1/users/me");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  acceptOrgDisclaimer: async (
    version: string
  ): Promise<OrgDisclaimerAcceptResponse> => {
    try {
      const response = await api.post(
        "/api/v1/users/consent/org-disclaimer",
        { version }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updateCurrentUser: async (userData: {
    first_name?: string | null;
    last_name?: string | null;
    email?: string;
  }) => {
    try {
      const response = await api.patch("/api/v1/users/me", userData);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updatePassword: async (passwordData: {
    current_password: string;
    new_password: string;
  }) => {
    try {
      const response = await api.patch(
        "/api/v1/users/me/password",
        passwordData
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  forgotPassword: async (email: string) => {
    try {
      const response = await api.post(`/api/v1/password-recovery/${email}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  resetPassword: async (
    token: string,
    new_password: string,
    confirm_new_password: string
  ) => {
    try {
      const response = await api.post("/api/v1/reset-password/", {
        token,
        new_password,
        confirm_new_password,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Email verification endpoints
  verifyEmail: async (token: string) => {
    try {
      const response = await api.post("/api/v1/users/verify", {
        token,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  resendVerificationEmail: async (email: string) => {
    try {
      const response = await api.post("/api/v1/users/verify/resend", {
        email,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getOnboardingStatus: async () => {
    try {
      const response = await api.get("/api/v1/users/me/onboarding-status");
      type BackendOnboardingStatus = {
        onboarding_completed?: boolean;
        redirect_to?: string;
        subscription_status?: string;
        user_id?: string;
        email?: string;
      };
      const data: BackendOnboardingStatus =
        response.data as BackendOnboardingStatus;
      // Normalize backend snake_case to frontend camelCase for consistency
      return {
        ...data,
        onboardingCompleted: Boolean(data?.onboarding_completed),
      };
    } catch (error) {
      throw handleApiError(error);
    }
  },

  deleteAccount: async (): Promise<{
    error_key: string;
    message: string;
    params: any;
  }> => {
    try {
      const response = await api.delete("/api/v1/users/me");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  refreshToken: async () => {
    try {
      const currentRefreshToken = getRefreshToken?.();
      if (!currentRefreshToken) {
        // Don't create error_key on frontend
        throw new Error("No refresh token available");
      }

      const response = await api.post("/api/v1/login/refresh-token", {
        refresh_token: currentRefreshToken,
      });

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  logout: async (
    accessToken: string | null,
    refreshToken: string | null,
    skipCloseAllSessions: boolean = false
  ) => {
    try {
      // Close all active chat sessions before logout (skip when free trial is expired)
      if (!skipCloseAllSessions) {
        try {
          await chatApi.closeAllSessions();
        } catch (closeSessionsError) {
          // Don't let close-all-sessions failure prevent the actual logout
          console.warn("Failed to close all sessions before logout:", closeSessionsError);
        }
      }

      // Call the backend logout endpoint to blacklist tokens
      // Send both tokens in the request body
      const response = await api.post("/api/v1/logout", {
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      return response.data;
    } catch (error) {
      // Even if backend logout fails, we should still log out the user
      // But we should still use handleApiError to get proper error structure
      throw handleApiError(error);
    }
  },
};

export const settingsApi = {
  // Get available settings options (languages and themes)
  getOptions: async () => {
    try {
      const response = await api.get("/api/v1/settings/options");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get current user's settings
  getMySettings: async () => {
    try {
      const response = await api.get("/api/v1/settings/me");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Update settings for current user (supports partial updates)
  updateMySettings: async (settings: { language?: string; theme?: string }) => {
    try {
      const response = await api.put("/api/v1/settings/me", settings);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

export const subscriptionApi = {
  // Get current user's subscription status and warnings
  getStatus: async (): Promise<SubscriptionStatusResponse> => {
    const response = await api.get("/api/v1/subscriptions/status");
    return response.data;
  },

  // Cancel the current user's subscription
  cancel: (reason?: string) => {
    const params = new URLSearchParams();
    if (reason) params.append("reason", reason);
    const url = `/api/v1/subscriptions/cancel${
      params.toString() ? `?${params.toString()}` : ""
    }`;
    return apiClient.post(url);
  },

  // Activate freemium trial without credit card
  activateFreemiumTrial: () => {
    return apiClient.post("/api/v1/subscriptions/freemium/activate");
  },

  // Get available subscription switch options for current user
  getSwitchOptions: () => apiClient.get("/api/v1/subscriptions/switch-options"),

  // Consume tokens for current user
  consumeTokens: (tokensUsed: number) => {
    const params = new URLSearchParams();
    params.append("tokens_used", tokensUsed.toString());
    return apiClient.post(
      `/api/v1/subscriptions/consume-tokens?${params.toString()}`
    );
  },

  // Get Stripe billing portal URL for managing payment methods (residual Stripe subscribers)
  getBillingPortal: async (): Promise<{ url: string }> => {
    const response = await api.post("/api/v1/subscriptions/billing-portal");
    return response.data;
  },
};

export const usageApi = {
  // Web visit signal (SPEC-020): fire-and-forget, 5 s timeout, abortable
  recordVisit: (visitId: string, signal?: AbortSignal): Promise<unknown> =>
    api.post(
      "/api/v1/usage/visits",
      { visit_id: visitId },
      { timeout: 5000, signal }
    ),
};

export const maintenanceApi = {
  getMaintenance: async (): Promise<MaintenanceApiResponse> => {
    const response = await api.get("/api/v1/maintenance", {
      headers: { Accept: "application/json" },
    });
    return response.data;
  },
};

export const questionnaireApi = {
  // Get current user's questionnaire
  getMyQuestionnaire: async () => {
    try {
      const response = await api.get("/api/v1/questionnaire/me");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get all answers for current user
  getMyAnswers: async () => {
    try {
      const response = await api.get("/api/v1/answers/");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Create or update answer for a specific step using POST /answers
  createAnswer: async (stepNumber: number, answerText: string) => {
    try {
      // Convert step number to question_key format (step 1 -> question_1)
      const questionKey = `question_${stepNumber}`;

      const response = await api.post("/api/v1/answers/", {
        question_key: questionKey,
        answer_text: answerText,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Submit/complete questionnaire - marks onboarding as completed
  submitQuestionnaire: async () => {
    try {
      const response = await api.post("/api/v1/questionnaire/submit");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get complete questionnaire data with answers
  getQuestionnaireData: async () => {
    try {
      // Fetch questionnaire and answers in parallel
      const [questionnaire, answers] = await Promise.all([
        questionnaireApi.getMyQuestionnaire(),
        questionnaireApi.getMyAnswers(),
      ]);

      return {
        questionnaire,
        answers,
      };
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get intro content after questionnaire submission
  getIntroContent: async (): Promise<
    import("@/types").QuestionnaireIntroContentResponse
  > => {
    try {
      const response = await api.get("/api/v1/questionnaire/intro-content");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

export const chatApi = {
  detectComposerPii: async (
    body: ComposerPiiDetectRequest,
    options: { signal?: AbortSignal } = {}
  ): Promise<ComposerPiiDetectResponse> => {
    const response = await api.post<ComposerPiiDetectResponse>(
      API_ENDPOINTS.CHAT.PII_DETECT,
      body,
      {
        signal: options.signal,
        timeout: 3000,
      }
    );
    return response.data;
  },

  // Streaming chat endpoint - main chat functionality
  // Based on POST /api/v1/chat/stream endpoint from OpenAPI spec
  streamChat: async (
    message: string,
    sessionId?: string | null,
    fileIds?: string[] | null,
    onMessage?: (chunk: any) => void,
    onError?: (error: any) => void,
    onComplete?: (sessionId: string) => void,
    abortSignal?: AbortSignal,
    answerForm?: "full" | "direct"
  ) => {
    // Internal function to perform the actual fetch request
    const performStreamRequest = async (authToken: string) => {
      const requestBody: ChatMessageRequest = {
        message: message.trim(),
        session_id: sessionId || null,
        file_ids: fileIds || null,
        ...(answerForm ? { answer_form: answerForm } : {}),
      };

      return await fetch(
        `${getApiBaseUrl()}/api/v1/chat/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            Accept: "text/event-stream",
            "Cache-Control": "no-cache",
          },
          body: JSON.stringify(requestBody),
          signal: abortSignal,
        }
      );
    };

    try {
      const token = getAuthToken?.();
      if (!token) {
        // Don't create error_key on frontend
        throw new Error("Authentication required");
      }

      // Validate required parameters
      if (!message || typeof message !== "string") {
        // Don't create error_key on frontend
        throw new Error("Message is required and must be a string");
      }

      // First attempt
      let response = await performStreamRequest(token);

      // Handle 401 - token expired, attempt refresh and retry
      if (response.status === 401) {
        const newToken = await attemptTokenRefresh();

        if (!newToken) {
          // Refresh failed - don't create error_key on frontend
          throw new Error("Authentication failed - please log in again");
        }

        // Retry with new token
        response = await performStreamRequest(newToken);
      }

      // Handle 430 - subscription/trial expired, redirect to pricing
      if (response.status === 430) {
        // Parse error data for proper error message
        let errorData: any = {};
        try {
          const errorText = await response.text();
          errorData = JSON.parse(errorText);
        } catch {
          errorData = {
            detail:
              "Your subscription or trial has expired. Please upgrade to continue.",
          };
        }

        const axiosLikeError = {
          isAxiosError: true,
          response: {
            status: 430,
            data: errorData,
          },
        };

        throw handleApiError(axiosLikeError);
      }

      if (!response.ok) {
        // For streaming errors, we need to parse the response body to get detailed error messages
        let errorData: any = {};

        try {
          // Try to parse the error response body
          const errorText = await response.text();
          errorData = JSON.parse(errorText);
        } catch (parseError) {
          // If we can't parse the error response, use a generic structure
          // Don't create error_key - let it be undefined
          console.warn("Failed to parse error response:", parseError);
          errorData = {
            detail: `HTTP error! status: ${response.status}`,
          };
        }

        // Create an error object that mimics axios error structure for handleApiError
        // Backend will provide error_key, params if available
        const axiosLikeError = {
          isAxiosError: true,
          response: {
            status: response.status,
            data: errorData, // Include full error data from backend (error_key, params, detail)
          },
        };

        throw handleApiError(axiosLikeError);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        // Don't create error_key on frontend
        throw new Error("No response body reader available");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let currentSessionId = sessionId;
      let processedChunks = new Set<string>(); // Track processed chunks to prevent duplicates
      let chunkSequence = 0; // Add sequence numbers to track order

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            if (onComplete && currentSessionId) {
              onComplete(currentSessionId);
            }
            break;
          }

          // Decode the chunk and add to buffer
          const textChunk = decoder.decode(value, { stream: true });
          buffer += textChunk;

          // Process complete lines (SSE format: "data: {json}\n\n")
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.trim() === "") continue;

            // Parse SSE format: "data: {json}"
            if (line.startsWith("data: ")) {
              const jsonStr = line.slice(6); // Remove "data: " prefix

              if (jsonStr.trim() === "[DONE]") {
                if (onComplete && currentSessionId) {
                  onComplete(currentSessionId);
                }
                return currentSessionId;
              }

              // Create unique identifier for this chunk to prevent duplicate processing
              const chunkId = `${++chunkSequence}-${jsonStr}`;
              if (processedChunks.has(chunkId)) {
                continue; // Skip duplicate chunks
              }
              processedChunks.add(chunkId);

              // Clean up processed chunks periodically to prevent memory leaks
              if (processedChunks.size > 1000) {
                processedChunks.clear();
              }

              try {
                const chunk = JSON.parse(jsonStr);

                // Validate chunk structure
                if (typeof chunk === "object" && chunk !== null) {
                  // Store session_id for future use
                  if (chunk.session_id && !currentSessionId) {
                    currentSessionId = chunk.session_id;
                  }

                  // Process chunk without verbose logging

                  if (onMessage) {
                    onMessage(chunk);
                  }
                } else {
                  console.warn("⚠️ Invalid chunk structure:", chunk);
                }
              } catch (parseError) {
                console.error(
                  "❌ Failed to parse chunk JSON:",
                  jsonStr,
                  parseError
                );
              }
            }
          }
        }
      } catch (streamError) {
        console.error("❌ Stream reading error:", streamError);
        if (onError) {
          onError(streamError);
        }
        throw streamError;
      } finally {
        reader.releaseLock();
      }

      return currentSessionId;
    } catch (error) {
      // If it's already an ApiError (from handleApiError), pass it through
      if ((error as ApiError).code) {
        if (onError) {
          onError(error);
        }
        throw error;
      }

      // For network-level errors (e.g. CORS blocking a 430 response),
      // fetch() throws TypeError before we can read response.status.
      // Proactively check subscription status to detect expired trial/subscription.
      if (
        error instanceof TypeError &&
        error.message.toLowerCase().includes("failed to fetch")
      ) {
        try {
          const status = await subscriptionApi.getStatus();
          const isExpired =
            status.subscription_status === "expired" ||
            status.subscription_status === "past_due" ||
            (status.subscription_status === "freemium" &&
              status.subscription_trial_remaining === 0) ||
            status.can_access_chat === false;

          if (isExpired) {
            const subscriptionError: ApiError = {
              code: ERROR_CODES.SUBSCRIPTION_EXPIRED,
              message:
                "Your subscription or trial has expired. Please upgrade to continue.",
            };

            // Trigger subscription expired redirect + toast
            if (subscriptionExpiredCallback) {
              subscriptionExpiredCallback(subscriptionError);
            }

            if (onError) {
              onError(subscriptionError);
            }
            throw subscriptionError;
          }
        } catch (statusCheckError) {
          // If the status check itself returned a subscription expired error, propagate it
          if (
            (statusCheckError as ApiError).code ===
            ERROR_CODES.SUBSCRIPTION_EXPIRED
          ) {
            throw statusCheckError;
          }
          // Status check failed for other reasons — fall through to generic error handling
        }
      }

      const apiError = handleApiError(error);
      if (onError) {
        onError(apiError);
      }
      throw apiError;
    }
  },

  // Non-streaming chat for testing
  sendMessage: async (
    message: string,
    sessionId?: string | null,
    fileIds?: string[] | null
  ) => {
    try {
      const response = await api.post("/api/v1/chat/", {
        message,
        session_id: sessionId,
        file_ids: fileIds,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Chat session management
  getSessions: async () => {
    try {
      const response = await api.get("/api/v1/chat-sessions/");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get messages from a specific session using transcript endpoint
  getSessionMessages: async (sessionId: number) => {
    try {
      const transcriptData: TranscriptResponse =
        await chatApi.getSessionTranscript(sessionId);

      // Transform transcript messages to ChatMessage format
      const messages =
        transcriptData.transcript?.map(
          (msg: TranscriptMessage, index: number) =>
            chatApi.transformTranscriptMessageToFrontend(msg, index, sessionId)
        ) || [];

      return messages;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Transform transcript message from backend to frontend ChatMessage format
  transformTranscriptMessageToFrontend: (
    transcriptMessage: TranscriptMessage,
    index: number,
    sessionId: number
  ) => {
    return {
      id: `${sessionId}-${index}-${transcriptMessage.role}`, // Create unique ID from session, index, and role
      role: transcriptMessage.role as "user" | "assistant",
      content: transcriptMessage.content || "",
      timestamp: transcriptMessage.timestamp
        ? new Date(transcriptMessage.timestamp)
        : new Date(),
      session_id: sessionId.toString(), // Convert to string for UI compatibility
      isStreaming: false,
      failed: false,
      ...(transcriptMessage.answer_form === "full" ||
      transcriptMessage.answer_form === "direct"
        ? { answer_form: transcriptMessage.answer_form }
        : {}),
    };
  },

  // Helper function to transform backend message format to frontend ChatMessage format
  transformBackendMessageToFrontend: (backendMessage: any) => {
    return {
      id: backendMessage.id || Date.now().toString(),
      role: backendMessage.role || "user",
      content: backendMessage.content || "",
      timestamp: backendMessage.timestamp
        ? new Date(backendMessage.timestamp)
        : new Date(),
      session_id: backendMessage.session_id || "",
      isStreaming: false,
      failed: false,
    };
  },

  createSession: async (title: string) => {
    try {
      const response = await api.post("/api/v1/chat-sessions/", {
        session_title: title,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get session metadata (separate from transcript)
  getSession: async (sessionId: number) => {
    try {
      const response = await api.get(`/api/v1/chat-sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get session transcript with messages
  getSessionTranscript: async (sessionId: number) => {
    try {
      const response = await api.get(
        `/api/v1/chat-sessions/${sessionId}/transcript`
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updateSession: async (
    sessionId: number,
    updates: {
      session_title?: string;
      deleted?: boolean;
      total_tokens?: number;
    }
  ) => {
    try {
      const response = await api.put(
        `/api/v1/chat-sessions/${sessionId}`,
        updates
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  deleteSession: async (sessionId: number) => {
    try {
      await api.delete(`/api/v1/chat-sessions/${sessionId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Session control endpoints
  closeSession: async (sessionId: string) => {
    try {
      const response = await api.post("/api/v1/chat/close-session", {
        session_id: sessionId,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  closeAllSessions: async () => {
    try {
      const response = await api.post("/api/v1/chat/close-all-sessions");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getActiveSessions: async () => {
    try {
      const response = await api.get("/api/v1/chat/active-sessions");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // File upload for chat - matches OpenAPI Body_files-upload_file schema
  uploadFile: async (
    file: File,
    description?: string,
    fileType: "user_upload" | "chat_storage" = "user_upload",
    onProgress?: (progress: number) => void
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file); // Required: binary file
      if (description) {
        formData.append("description", description); // Optional: string | null
      }
      formData.append("file_type", fileType); // Optional: FileType enum, default 'user_upload'

      const response = await api.post("/api/v1/files/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(progress);
          }
        },
      });

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Upload multiple files to session endpoint
  uploadMultipleFilesToSession: async (
    files: File[],
    sessionId?: string | null
  ): Promise<import("@/types").MultipleFileUploadResponse> => {
    // Internal function to perform the actual upload request
    const performUploadRequest = async (authToken: string) => {
      // Create FormData for multipart/form-data
      const formData = new FormData();

      // Add session_id (can be null for new sessions)
      if (sessionId) {
        formData.append("session_id", sessionId);
      }

      // Add file_type as chat_storage for all files
      formData.append("file_type", "chat_storage");

      // Add all files
      files.forEach((file) => {
        formData.append("files", file);
      });

      return await fetch(
        `${getApiBaseUrl()}/api/v1/files/upload-multiple-to-session`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            // Don't set Content-Type header for FormData - browser will set it with boundary
          },
          body: formData,
        }
      );
    };

    try {
      const token = getAuthToken?.();
      if (!token) {
        // Don't create error_key on frontend
        throw new Error("Authentication required");
      }

      // First attempt
      let response = await performUploadRequest(token);

      // Handle 401 - token expired, attempt refresh and retry
      if (response.status === 401) {
        const newToken = await attemptTokenRefresh();

        if (!newToken) {
          // Refresh failed - don't create error_key on frontend
          throw new Error("Authentication failed - please log in again");
        }

        // Retry with new token
        response = await performUploadRequest(newToken);
      }

      // Handle 430 - subscription/trial expired, redirect to pricing
      if (response.status === 430) {
        const errorData = await response.json().catch(() => ({
          detail:
            "Your subscription or trial has expired. Please upgrade to continue.",
        }));

        const axiosLikeError = {
          isAxiosError: true,
          response: {
            status: 430,
            data: errorData,
          },
        };

        throw handleApiError(axiosLikeError);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Create an error object that mimics axios error structure for handleApiError
        // Backend will provide error_key, params if available
        const axiosLikeError = {
          isAxiosError: true,
          response: {
            status: response.status,
            data: errorData, // Use backend error data as-is (error_key, params, detail)
          },
        };

        throw handleApiError(axiosLikeError);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // If it's already an ApiError from handleApiError, just throw it
      if ((error as any).code) {
        throw error;
      }

      // For network-level errors (e.g. CORS blocking a 430 response),
      // check subscription status to detect expired trial/subscription
      if (
        error instanceof TypeError &&
        (error as TypeError).message.toLowerCase().includes("failed to fetch")
      ) {
        try {
          const status = await subscriptionApi.getStatus();
          const isExpired =
            status.subscription_status === "expired" ||
            status.subscription_status === "past_due" ||
            (status.subscription_status === "freemium" &&
              status.subscription_trial_remaining === 0) ||
            status.can_access_chat === false;

          if (isExpired) {
            const subscriptionError: ApiError = {
              code: ERROR_CODES.SUBSCRIPTION_EXPIRED,
              message:
                "Your subscription or trial has expired. Please upgrade to continue.",
            };
            if (subscriptionExpiredCallback) {
              subscriptionExpiredCallback(subscriptionError);
            }
            throw subscriptionError;
          }
        } catch (statusCheckError) {
          if (
            (statusCheckError as ApiError).code ===
            ERROR_CODES.SUBSCRIPTION_EXPIRED
          ) {
            throw statusCheckError;
          }
        }
      }

      // Otherwise, wrap it with handleApiError
      throw handleApiError(error);
    }
  },
};

export const faqApi = {
  // Get all FAQs
  getFAQs: async () => {
    try {
      const response = await api.get("/api/v1/faq");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Search FAQs
  searchFAQs: async (query: string) => {
    try {
      const response = await api.get("/api/v1/faq/search", {
        params: { q: query },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Voice API
export const voiceApi = {
  /**
   * Transcribe audio to text using speech-to-text (STT)
   * Endpoint: POST /api/v1/chat/stt
   *
   * @param audioBlob - The recorded audio blob
   * @param format - Audio format (webm, mp4, wav, etc.)
   * @param sessionId - Optional session ID for context
   * @param language - Optional language code for transcription
   * @param autoSend - Whether to automatically send the transcribed message
   * @returns Promise resolving to transcribed text
   */
  transcribe: async (
    audioBlob: Blob,
    format: string,
    sessionId?: string | null,
    language?: string | null,
    autoSend: boolean = false
  ): Promise<import("@/features/chat/types").VoiceTranscriptionResponse> => {
    try {
      // Create FormData for multipart/form-data upload
      const formData = new FormData();

      // Add the audio file with proper naming
      formData.append("file", audioBlob, `recording.${format}`);

      // Add optional parameters
      if (language) {
        formData.append("language", language);
      }

      if (sessionId) {
        formData.append("session_id", sessionId);
      }

      // Add auto_send flag
      formData.append("auto_send", autoSend.toString());

      const response = await api.post("/api/v1/chat/stt", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      console.error("❌ [Voice STT] Transcription error:", error);
      throw handleApiError(error);
    }
  },

  /**
   * Convert text to speech (TTS) and get audio URL
   * Endpoint: POST /api/v1/chat/tts
   *
   * @param text - The text to convert to speech
   * @param voice - Voice identifier for TTS
   * @param locale - Locale/language code (e.g., 'en-US', 'fr-FR')
   * @param sessionId - Optional session ID for context
   * @returns Promise resolving to audio URL (signed blob URL)
   */
  synthesize: async (
    text: string,
    voice?: string,
    locale?: string,
    sessionId?: string | null
  ): Promise<import("@/features/chat/types").VoiceSynthesisResponse> => {
    try {
      const requestBody: {
        text: string;
        voice?: string;
        locale?: string;
        session_id?: string;
      } = {
        text,
      };

      requestBody.voice = "en-US-JennyNeural";
      requestBody.locale = "en-US";

      //Add optional parameters
      if (voice) {
        requestBody.voice = voice;
      }

      if (locale) {
        requestBody.locale = locale;
      }

      if (sessionId) {
        requestBody.session_id = sessionId;
      }

      const response = await api.post("/api/v1/chat/tts", requestBody);

      return response.data;
    } catch (error) {
      console.error("❌ [Voice TTS] Synthesis error:", error);
      throw handleApiError(error);
    }
  },
};

export const knowledgeFilesApi = {
  // Upload a knowledge file
  uploadFile: async (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<any> => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        "/api/v1/knowledge-files/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(progress);
            }
          },
        }
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get paginated list of knowledge files
  getFiles: async ({
    skip = 0,
    limit = 10,
    orderBy,
    order = "desc",
  }: {
    skip?: number;
    limit?: number;
    orderBy?: string | null;
    order?: "asc" | "desc";
  } = {}): Promise<KnowledgeFileListResponse> => {
    try {
      const params: Record<string, any> = {
        skip,
        limit,
        order,
      };

      // Only include order_by if it's provided
      if (orderBy) {
        params.order_by = orderBy;
      }

      const response = await api.get<KnowledgeFileListResponse>(
        "/api/v1/knowledge-files/",
        {
          params,
        }
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Delete a knowledge file
  deleteFile: async (fileId: string): Promise<{ success: boolean }> => {
    try {
      const response = await api.delete(`/api/v1/knowledge-files/${fileId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get file processing status
  getFileStatus: async (
    fileId: string
  ): Promise<{ id: string; status: string; processingStatus?: string }> => {
    try {
      // TODO: Replace with actual endpoint when backend is ready
      // const response = await api.get(`/api/v1/knowledge-files/${fileId}/status`);
      // return response.data;

      // DUMMY RESPONSE - Return completed status
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            id: fileId,
            status: "completed",
            processingStatus: "ready",
          });
        }, 300);
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

export const authTestUtils = {
  // Test the complete authentication flow
  testAuthFlow: async (email: string, password: string) => {
    try {
      // Step 1: Login
      const loginResult = await authApi.login(email, password);

      // Step 2: Test token by getting current user
      const userData = await authApi.getCurrentUser();

      // Step 3: Get onboarding status
      const onboardingStatus = await authApi.getOnboardingStatus();

      return {
        success: true,
        token: loginResult,
        user: userData,
        onboarding: onboardingStatus,
      };
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Test logout flow with session cleanup
  testLogoutFlow: async () => {
    try {
      const accessToken: string | null = getAuthToken ? getAuthToken() : null;
      const refreshToken: string | null = getRefreshToken
        ? getRefreshToken()
        : null;
      const logoutResult = await authApi.logout(accessToken, refreshToken);

      return {
        success: true,
        result: logoutResult,
      };
    } catch (error) {
      throw handleApiError(error);
    } finally {
      console.groupEnd();
    }
  },

  // Test individual endpoints
  testEndpoints: async () => {
    console.group("🌐 Testing Individual Endpoints");

    const tests = [
      { name: "Health Check", url: "/api/v1/utils/health-check/" },
      {
        name: "Test Token (should fail without auth)",
        endpoint: () => authApi.testToken(),
      },
      {
        name: "Get User (should fail without auth)",
        endpoint: () => authApi.getCurrentUser(),
      },
      {
        name: "Onboarding Status (should fail without auth)",
        endpoint: () => authApi.getOnboardingStatus(),
      },
    ];

    for (const test of tests) {
      try {
        if (test.url) {
          await api.get(test.url);
        } else if (test.endpoint) {
          await test.endpoint();
        }
      } catch (error: any) {
        // Log the handled error
        const handledError = handleApiError(error);
        console.error("❌ Test failed:", handledError);
      }
    }

    console.groupEnd();
  },
};

export const chatTestUtils = {
  // Test streaming chat functionality
  testStreamingChat: async (
    message: string = "Hello, can you help me with something?"
  ) => {
    console.group("💬 Testing Chat Streaming");

    try {
      let messageCount = 0;
      let finalSessionId = "";

      const sessionId = await chatApi.streamChat(
        message,
        null, // No session ID - should create new one
        null, // No file IDs

        // onError callback
        (error) => {
          const handledError = handleApiError(error);
          console.error("❌ Streaming error:", handledError);
        },

        // onComplete callback
        (sessionId) => {
          finalSessionId = sessionId;
        }
      );

      return {
        success: true,
        sessionId: finalSessionId || sessionId,
        messageCount,
      };
    } catch (error) {
      throw handleApiError(error);
    } finally {
      console.groupEnd();
    }
  },

  // Test complete chat flow with session management
  testCompleteFlow: async () => {
    console.group("🔄 Testing Complete Chat Flow");

    try {
      // Test 1: Send first message (creates session)
      const result1 = await chatTestUtils.testStreamingChat("Hello HARVN!");

      if (!result1.success) {
        throw new Error("First message test failed");
      }

      // Test 2: Send follow-up message (uses existing session)
      const result2 = await chatTestUtils.testStreamingChat(
        "How are you doing?"
      );

      if (!result2.success) {
        throw new Error("Follow-up message test failed");
      }

      // Test 3: Get active sessions
      const activeSessions = await chatApi.getActiveSessions();

      return {
        success: true,
        sessions: [result1.sessionId, result2.sessionId],
        activeSessions,
      };
    } catch (error) {
      throw handleApiError(error);
    } finally {
      console.groupEnd();
    }
  },

  // Test streaming chat functionality with enhanced debugging
  testStreamingChatDebug: async (
    message: string = "Hello, can you help me with something?"
  ) => {
    console.group(
      "💬 Enhanced Chat Streaming Debug Test (Deduplication Enabled)"
    );

    try {
      let chunkCount = 0;
      let totalContent = "";
      let duplicateChunksSkipped = 0;
      let duplicateContentSkipped = 0;
      let finalSessionId = "";
      let lastChunkContent = "";

      const returnedSessionId = await chatApi.streamChat(
        message,
        null, // No session ID - should create new one
        null, // No file IDs

        // onMessage callback with duplication detection
        (chunk) => {
          chunkCount++;
          const content = chunk.content || "";

          // Track duplicate detection
          if (content === lastChunkContent && content.length > 0) {
            duplicateChunksSkipped++;
            console.warn(
              `⚠️ DUPLICATE CHUNK CONTENT: "${content}" (chunk #${chunkCount})`
            );
          }

          // Track content to detect duplication patterns
          if (chunk.role === "assistant") {
            // Check if this content would create duplication
            if (totalContent.endsWith(content) && content.length > 0) {
              duplicateContentSkipped++;
              console.warn(
                `⚠️ CONTENT DUPLICATION WOULD OCCUR: "${content}" already at end of total content`
              );
            } else {
              totalContent += content;
            }
          }

          lastChunkContent = content;
        },

        // onError callback
        (error) => {
          const handledError = handleApiError(error);
          console.error("❌ Error:", handledError);
        },

        // onComplete callback
        (sessionId) => {
          finalSessionId = sessionId;

          if (duplicateChunksSkipped > 0 || duplicateContentSkipped > 0) {
            console.warn("⚠️ DUPLICATION SUMMARY:", {
              duplicateChunksSkipped,
              duplicateContentSkipped,
              message:
                "Deduplication system is working - duplicates were prevented!",
            });
          }
        }
      );

      return {
        success: true,
        sessionId: returnedSessionId || finalSessionId,
        chunkCount,
        totalContentLength: totalContent.length,
        duplicateChunksSkipped,
        duplicateContentSkipped,
        duplicateRate: Math.round((duplicateChunksSkipped / chunkCount) * 100),
      };
    } catch (error) {
      throw handleApiError(error);
    } finally {
      console.groupEnd();
    }
  },
};

export default api;
